import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Shuffle, Eye, EyeOff, Loader2, Building2, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const registerSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(6, "Minimum 6 caractères"),
  company_name: z.string().min(2, "Nom requis"),
  contact_name: z.string().min(2, "Nom du contact requis"),
  client_type: z.string().default("entreprise"),
});

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clientType, setClientType] = useState("entreprise");

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      client_type: "entreprise"
    }
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await registerUser({ ...data, client_type: clientType });
      toast.success("Compte créé avec succès !");
      navigate("/dashboard");
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Erreur lors de l'inscription";
      if (errorMessage.includes("already registered") || errorMessage.includes("déjà")) {
        toast.error("Cet email est déjà utilisé. Connectez-vous ou utilisez un autre email.");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Shuffle className="w-5 h-5 text-white" />
          </div>
          <span className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit' }}>TIREO</span>
        </Link>

        <Card className="border-border shadow-lg animate-fade-in-up">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>Créer un compte</CardTitle>
            <CardDescription>Premier tirage et premier sondage offerts</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Type d'organisation */}
              <div className="space-y-2">
                <Label>Type d'organisation</Label>
                <Select
                  value={clientType}
                  onValueChange={(value) => {
                    setClientType(value);
                    setValue("client_type", value);
                  }}
                >
                  <SelectTrigger className="h-11 bg-slate-50 dark:bg-slate-800 border-transparent focus:border-primary text-foreground" data-testid="register-type-select">
                    <SelectValue placeholder="Sélectionnez..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="entreprise">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        <span>Entreprise</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="association">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span>Association</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name">
                  {clientType === "association" ? "Nom de l'association" : "Nom de l'entreprise"}
                </Label>
                <Input
                  id="company_name"
                  type="text"
                  placeholder={clientType === "association" ? "Mon Association" : "ACME Corp"}
                  className="h-11 bg-slate-50 dark:bg-slate-800 border-transparent focus:border-primary text-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  data-testid="register-company-input"
                  {...register("company_name")}
                />
                {errors.company_name && (
                  <p className="text-sm text-destructive">{errors.company_name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_name">Nom du responsable</Label>
                <Input
                  id="contact_name"
                  type="text"
                  placeholder="Jean Dupont"
                  className="h-11 bg-slate-50 dark:bg-slate-800 border-transparent focus:border-primary text-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  data-testid="register-contact-input"
                  {...register("contact_name")}
                />
                {errors.contact_name && (
                  <p className="text-sm text-destructive">{errors.contact_name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  className="h-11 bg-slate-50 dark:bg-slate-800 border-transparent focus:border-primary text-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  data-testid="register-email-input"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="h-11 bg-slate-50 dark:bg-slate-800 border-transparent focus:border-primary text-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500 pr-10"
                    data-testid="register-password-input"
                    {...register("password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 rounded-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  "Créer mon compte"
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              Déjà un compte ?{" "}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Se connecter
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
